import { useEffect, useState, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * Глобальный промпт обновления PWA.
 *
 * Поведение:
 *  1. SW регистрируется автоматически на mount.
 *  2. Каждые 60s дёргаем updateServiceWorker(false) — пустая проверка,
 *     чтобы фоном узнать о новой версии. Не вызывает reload.
 *  3. Когда workbox нашёл новый SW (needRefresh = true):
 *     - Показываем золотой попап внизу экрана с нашим стилем: «♻ Вышло обновление»
 *     - Через 2.5s вызываем updateServiceWorker(true)
 *       → workbox делает skipWaiting + clientsClaim → страница перезагружается.
 *  4. Когда SW «готов к офлайну» (offlineReady = true) — короткий неблокирующий тост.
 *
 * Без кнопок. Полностью автоматически.
 */
export function PwaUpdatePrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showOffline, setShowOffline] = useState(false);
  const hideTimerRef = useRef<number | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error: unknown) {
      // eslint-disable-next-line no-console
      console.warn('[PWA] SW register error', error);
    },
  });

  // Polling обновлений — раз в 60s.
  useEffect(() => {
    const id = window.setInterval(() => {
      updateServiceWorker(false).catch(() => {});
    }, 60_000);
    return () => window.clearInterval(id);
  }, [updateServiceWorker]);

  useEffect(() => {
    if (!needRefresh) {
      setShowPrompt(false);
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      return;
    }
    setShowPrompt(true);
    hideTimerRef.current = window.setTimeout(() => {
      setNeedRefresh(false);
      updateServiceWorker(true).catch(() => {
        window.location.reload();
      });
    }, 2500);
    return () => {
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [needRefresh, setNeedRefresh, updateServiceWorker]);

  useEffect(() => {
    if (!offlineReady) {
      setShowOffline(false);
      return;
    }
    setShowOffline(true);
    const id = window.setTimeout(() => setShowOffline(false), 2500);
    return () => window.clearTimeout(id);
  }, [offlineReady]);

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-6 z-[60] flex justify-center px-4 sm:bottom-8"
      >
        {showPrompt && (
          <div
            className="pointer-events-auto flex items-center gap-3 rounded-2xl
                       bg-gradient-to-b from-stone-900/95 to-stone-950/95
                       backdrop-blur-xl
                       ring-1 ring-amber-400/60
                       shadow-[0_8px_32px_rgba(0,0,0,0.5),0_0_24px_rgba(251,191,36,0.25),inset_0_1px_0_rgba(255,255,255,0.08)]
                       px-4 py-3 text-white
                       animate-[pwa-slide-up_360ms_cubic-bezier(0.16,1,0.3,1)_both]"
            data-testid="pwa-update-prompt"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full
                            bg-gradient-to-b from-amber-300 to-amber-500
                            text-stone-950
                            shadow-[0_2px_8px_rgba(251,191,36,0.5),inset_0_1px_0_rgba(255,255,255,0.4)]">
              <span className="text-base font-bold">♻</span>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-semibold text-amber-200">
                Вышло обновление
              </span>
              <span className="text-[11px] text-white/60">
                Применяется автоматически…
              </span>
            </div>
            <span className="ml-2 h-5 w-5 animate-spin rounded-full border-2 border-amber-300/30 border-t-amber-300" />
          </div>
        )}
      </div>

      {showOffline && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed bottom-4 left-1/2 z-[60] -translate-x-1/2
                     rounded-full bg-stone-900/90 px-3 py-1.5 text-[11px] text-emerald-200
                     ring-1 ring-emerald-400/30 backdrop-blur
                     animate-[pwa-slide-up_240ms_ease-out_both]"
        >
          ✓ Готово к офлайну
        </div>
      )}
    </>
  );
}
